import { useCallback } from 'react';

import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useBudgetGetDistributorGroupInfo } from '@/hooks/useBudgetGetDistributorGroupInfo';
import { useDistributorGetDistributorGroupInfo } from '@/hooks/useDistributorGetDistributorGroupInfo';
import { GroupType } from '@/shared/types/groupType';

export interface BudgetConflict {
  hasConflict: boolean;
  conflictGroupId?: number;
  conflictGroupName?: string;
  conflictGroupType?: GroupType;
}

interface UseBudgetConflictDetectionReturn {
  checkBudgetConflict: (userId: string, targetGroupId: number) => Promise<BudgetConflict>;
}

export function useBudgetConflictDetection(): UseBudgetConflictDetectionReturn {
  const { organizationId } = useOrganizationId();
  const { getDistributorGroupInfo: getDistributorGroupBudgetInfo } = useBudgetGetDistributorGroupInfo();
  const { getDistributorGroupInfo: getDistributorGroupDistributorInfo } = useDistributorGetDistributorGroupInfo();

  const checkBudgetConflict = useCallback(
    async (userId: string, targetGroupId: number): Promise<BudgetConflict> => {
      try {
        const distributorInfo = await getDistributorGroupDistributorInfo(organizationId, userId);

        if (distributorInfo) {
          return {
            hasConflict: true,
            conflictGroupId: distributorInfo.systemId,
            conflictGroupName: distributorInfo.distributorName || `Distributor Group ${distributorInfo.systemId}`,
            conflictGroupType: 'distributor',
          };
        }

        const budgetInfo = await getDistributorGroupBudgetInfo(organizationId, userId);

        if (budgetInfo && budgetInfo.id !== targetGroupId) {
          return {
            hasConflict: true,
            conflictGroupId: budgetInfo.id,
            conflictGroupName: budgetInfo.name || `Budget Group ${budgetInfo.id}`,
            conflictGroupType: 'budget',
          };
        }

        return { hasConflict: false };
      } catch (err) {
        console.error('Error checking budget conflict:', err);
        return { hasConflict: false };
      }
    },
    [organizationId, getDistributorGroupBudgetInfo, getDistributorGroupDistributorInfo],
  );

  return {
    checkBudgetConflict,
  };
}
